import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { iacOptionInterface } from 'src/app/page_component_item/input-with-autocomplete/input-with-autocomplete.component';
import { ProfileService } from 'src/app/services/profile/profile.service';
import { customerInterface } from 'src/app/util/interfaces/customer';
import { enduserInterface } from 'src/app/util/interfaces/enduser';
import { genericFormI } from 'src/app/util/interfaces/genericFormRMK';
import { localeInterface } from 'src/app/util/interfaces/locale';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faArrowLeft, faInfoCircle, faQuestionCircle, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import { CheckboxModule } from 'src/app/page_component_item/checkbox/checkbox.module';
import { GlobalService } from 'src/app/services/global/global.service';
import { NotificationService } from 'src/app/services/notification/notification.service';
import { DotBounceLoadingEffectModule } from 'src/app/page_component_item/dot-bounce-loading-effect/dot-bounce-loading-effect.module';
import { PermissionService } from 'src/app/util/services/permission/permission.service';
import { GenericFormGenModule } from 'src/app/page_component_item/generic-form-gen/generic-form-gen.module';
import { BehalfOfService } from 'src/app/services/behalfOf/behalf-of.service';
import { InputWithAutocompleteModule } from 'src/app/page_component_item/input-with-autocomplete/input-with-autocomplete.module';
import { AuthLoginService } from 'src/app/page/auth-page/service/auth-login.service';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { PasswordResetComponent } from 'src/app/components/menu-profile/components/password-reset/password-reset.component';
import { OutOfOfficeComponent } from 'src/app/components/menu-profile/components/out-of-office/out-of-office.component';
import { initBehalfOfForm } from './data/behalfOf.form';
import { SessionStatus } from 'src/app/services/profile/sessionStatus.enum';
import { MenuLateralOptionsComponent } from './components/menu-lateral-options/menu-lateral-options.component';
import { ProfileSignal } from 'src/app/services/profile/profile.signal';

@Component({
	selector: 'app-menu-profile',
	standalone: true,
	imports: [
		CommonModule,
		FaIconComponent,
		CheckboxModule,
		DotBounceLoadingEffectModule,
		GenericFormGenModule,
		InputWithAutocompleteModule,
		PasswordResetComponent,
		OutOfOfficeComponent,
		MenuLateralOptionsComponent,
	],
	templateUrl: './menu-profile.component.html',
	styleUrl: './menu-profile.component.scss',
})
export class MenuProfileComponent implements OnInit {
	protected readonly profileService = inject(ProfileService);
	protected readonly profileSignal = inject(ProfileSignal);
	protected readonly globalService = inject(GlobalService);
	protected readonly notificationService = inject(NotificationService);
	protected readonly permissionService = inject(PermissionService);
	protected readonly behalfOfService = inject(BehalfOfService);
	protected readonly authLoginService = inject(AuthLoginService);
	protected readonly Router = inject(Router);

	icons = {
		faArrowLeft: faArrowLeft,
		faTimes: faTimes,
		faInfoCircle: faInfoCircle,
		faTrash: faTrash,
		faQuestionCircle: faQuestionCircle,
	};

	@Input() labels: { [key: string]: string } = {};

	isProfileMenuOpen = undefined;
	@Input() user: enduserInterface;
	userCopy: enduserInterface;
	language: string[];
	locale: localeInterface[];
	canReloadPage = false;
	isAnalystView: boolean;
	@Input() availableCustomers: customerInterface[] = [];

	userDateFormat = 'YMD HMS';
	darkModeOn = false;

	profileMenuView: 'profile' | 'passwordReset' | 'outOfOffice' = 'profile';
	logoImagePath = '';

	behalfOfForm: genericFormI[] = initBehalfOfForm();
	allowedBehalfOfUsers: enduserInterface[] = [];
	allowedBehalfOfUsersIAC: iacOptionInterface[] = [];
	selectedBehalfOfUser: enduserInterface = { fullName: '', email: '' };
	searchInputText = '';

	receiveEmailLog = true;
	showAwaitingAuthorization = false;
	awaitingAuthorizationMemory = false;

	isMobile = true;

	isLoadingSubmit = false;
	isLoadingTheme = false;
	isLoadingBehalfOf = false;
	isEnteringBehalfOfMode = false;
	isLoadingLocales = false;

	ngOnInit(): void {
		this.userCopy = JSON.parse(JSON.stringify(this.user));
		this.isAnalystView = this.profileService.userData().sessionStatus == SessionStatus.ANALYST;

		this.profileService.getLocales().subscribe(
			(localeData) => {
				this.locale = localeData;
				this.isLoadingLocales = false;
			},
			() => {
				this.isLoadingLocales = false;
			}
		);

		setTimeout(() => {
			if (this.user && this.user.showAwaitingAuthorization == 1) {
				this.showAwaitingAuthorization = this.user.showAwaitingAuthorization == 1;
				this.awaitingAuthorizationMemory = true;
			}
		}, 1000);

		this.receiveEmailLog =
			this.profileService.userData().permission && this.profileService.userData().permission.includes('receive_emails_log_off')
				? false
				: true;

		this.profileService.getAvailableCustomers().subscribe({
			next: (customers) => {
				this.availableCustomers = customers;
				const customerGEN: genericFormI['items'][] = [];
				for (const customer of this.availableCustomers) {
					customerGEN.push({ name: customer.customer, label: customer.customer, values: [customer.id] });
				}
				this.behalfOfForm.find((form) => form.name == 'customer')?.items?.push(...customerGEN);
			},
		});

		this.userDateFormat = `${this.profileService.getDateFormat()} ${this.profileService.getDateTimeFormat()}`;

		this.globalService.darkModeOn.subscribe({
			next: (darkModeBool) => {
				darkModeBool == true ? (darkModeBool = true) : (darkModeBool = false); //This is to format in case the subscribe return true as 1;
				this.darkModeOn = darkModeBool;
				localStorage.setItem('darkMode', this.darkModeOn.toString());
				document.documentElement.style.colorScheme = this.darkModeOn == true ? 'dark' : 'light';
			},
		});
	}

	notifyPageReload(): void {
		this.notificationService.setNotificationMessage(this.labels.lbl_action_reload_page);
	}

	changeThemeExample(styleId: string): void {
		this.isLoadingTheme = true;
		this.profileService.getStyleSheetData(styleId).subscribe((styleData) => {
			const root = document.documentElement;
			this.isLoadingTheme = false;
			// if(styleData && styleData.cssItems) {
			//   styleData.cssItems.forEach( style => {
			//     if(style.cssItem === 'primary-color-1') {
			//       root.style.setProperty(`--tempThemeButton`, style.cssItemValue);
			//     } if(style.cssItem === 'primary-color-3') {
			//       root.style.setProperty(`--tempThemeButtonHover`, style.cssItemValue);
			//     } if(style.cssItem === 'primary-color-2') {
			//       root.style.setProperty(`--tempThemeButton2`, style.cssItemValue);
			//     } if(style.cssItem === 'primary-color-3') {
			//       root.style.setProperty(`--tempThemeButton2Hover`, style.cssItemValue);
			//     }
			//   })
			// }
		});
	}

	getAllowedBehalfOfUsers(userName: string, userEmail: string, idCustomer: string): void {
		this.isLoadingBehalfOf = true;

		this.searchInputText = '';
		if (userName && userName.trim() !== '') {
			this.searchInputText = userName;
		} else if (userEmail && userEmail.trim() !== '') {
			this.searchInputText = userEmail;
		}

		this.behalfOfService.getAllowedBehalfOfUsers(userName, userEmail, idCustomer).subscribe(
			(behalfOfUsers) => {
				this.allowedBehalfOfUsers = [];
				this.allowedBehalfOfUsersIAC = [];
				this.allowedBehalfOfUsers = behalfOfUsers;
				for (const user of this.allowedBehalfOfUsers) {
					const behalfOfIAC: iacOptionInterface = {
						name: user.fullName,
						secondName: user.email,
						value: user.email,
						commonGroup: user.email,
					};
					this.allowedBehalfOfUsersIAC.push(behalfOfIAC);
				}

				this.isLoadingBehalfOf = false;
			},
			() => {
				this.isLoadingBehalfOf = false;
			}
		);
	}

	enterBehalfOfMode(): void {
		if (this.isEnteringBehalfOfMode || !this.selectedBehalfOfUser.email || this.selectedBehalfOfUser.email == '') return;
		this.isEnteringBehalfOfMode = true;
		this.behalfOfService.enterBehalfOfMode(this.selectedBehalfOfUser.email).subscribe(() => {
			localStorage.setItem('updateEvent', Math.random().toString());
			location.reload();
		});
	}

	leaveBehalfOfMode(): void {
		if (this.isEnteringBehalfOfMode) return;
		this.isEnteringBehalfOfMode = true;
		this.behalfOfService.leaveBehalfOfMode().subscribe(() => {
			localStorage.setItem('updateEvent', Math.random().toString());
			location.reload();
		});
	}

	addEnduserContact(): void {
		const newContact = { id: '', contact_type: 'WPP', contact_value: '' };
		this.userCopy.contacts.push($.extend(true, {}, newContact));
		this.user.contacts.push($.extend(true, {}, newContact));
	}

	removeEnduserContact(index: number): void {
		const deleteBody = $.extend(true, {}, this.user.contacts[index]);
		if (deleteBody.id !== '') {
			const deleteBodyArray = [];
			deleteBodyArray.push(deleteBody);
			this.notificationService.setNotificationMessage(this.labels.lbl_geral_deletado_com_sucesso);
			this.profileService.deleteEnduserContacts(deleteBodyArray).subscribe();
		}
		this.userCopy.contacts.splice(index, 1);
		this.user.contacts.splice(index, 1);
	}

	handleSaveContacts(): void {
		const postArray = [];
		const putArray = [];

		if (this.userCopy.contacts) {
			for (const contactCopy of this.userCopy.contacts) {
				if (contactCopy.id === '') {
					postArray.push(contactCopy);
				} else {
					for (const contact of this.user.contacts) {
						if (contact.contact_type !== contactCopy.contact_type || contact.contact_value !== contactCopy.contact_value) {
							putArray.push(contactCopy);
						}
					}
				}
			}
		}

		if (postArray.length > 0) {
			this.profileService.postEnduserContacts(postArray).subscribe();
		}
		if (putArray.length > 0) {
			this.profileService.putEnduserContacts(putArray).subscribe();
		}
	}

	saveDateFormat(dateFormat: string): void {
		localStorage.setItem('dateFormat', dateFormat);
	}

	updateEnduserData(): void {
		if (this.isLoadingSubmit === false) {
			this.isLoadingSubmit = true;

			const enduserUpdateBody = {
				language: '' + this.userCopy.language,
				location: this.user.location,
				idcust: this.userCopy.idCustomer,
				idCustomer: this.userCopy.idCustomer,
				site: this.user.site,
				last_access: Date.now(),
				phone: '' + this.userCopy.phone,
				uniquekey: this.user.uniquekey,
				email: '' + this.userCopy.email,
				gmt: this.user.gmt,
				last_password: this.user.last_password,
				locale: '' + this.userCopy.locale,
				solution_area_default: this.user.solution_area_default,
				usertype: this.user.usertype,
				customer: this.user.customer,
				css_pattern: '' + this.userCopy.css_pattern.toString(),
				login: this.user.login,
				id: this.user.id,
				fullName: this.user.fullName,
				mobile: '' + this.userCopy.mobile,
				sessionStatus: this.userCopy.sessionStatus,
				endusers_contacts: [],
			};

			const phoneNumberRegex = new RegExp(String.raw`^(\+?\d{2,3})?(\d{2,3}|\(\d{2,3}\))?\d?\d{4}-?\d{4}$`);
			if (enduserUpdateBody.phone && !phoneNumberRegex.test(enduserUpdateBody.phone.replaceAll(/\s/g, ''))) {
				this.notificationService.setNotificationMessage(this.labels.lbl_invalid_phone);
				this.isLoadingSubmit = false;
			} else if (enduserUpdateBody.mobile && !phoneNumberRegex.test(enduserUpdateBody.mobile.replaceAll(/\s/g, ''))) {
				this.notificationService.setNotificationMessage(this.labels.lbl_invalid_phone);
				this.isLoadingSubmit = false;
			} else {
				if (
					this.user.language != enduserUpdateBody.language ||
					this.user.css_pattern != enduserUpdateBody.css_pattern ||
					this.user.locale != enduserUpdateBody.locale
				) {
					this.canReloadPage = true;
				}

				this.handleSaveContacts();
				this.profileService.updateUser(enduserUpdateBody).subscribe(() => {
					if (this.userCopy.sessionStatus.toString() != localStorage.getItem('sessionStatus')) {
						this.authLoginService.setSessionStatus(
							this.user.sessionStatus.toString() ? this.user.sessionStatus.toString() : this.user.idAnalyst ? '1' : '0'
						);
						this.canReloadPage = true;
					}

					if (this.showAwaitingAuthorization != this.awaitingAuthorizationMemory) {
						this.canReloadPage = true;
					}

					this.profileService.setLocalUser().subscribe((userData) => {
						if (this.user.idCustomer != enduserUpdateBody.idCustomer) {
							window.history.pushState(null, '', userData.main_menu);
						}

						this.isLoadingSubmit = false;
						this.notificationService.setNotificationMessage(this.labels.lbl_successful_updated);

						if (this.canReloadPage) {
							localStorage.setItem('updateEvent', Math.random().toString());
							location.href = this.Router.url.split('?')[0];
						}

						if (this.user.idCustomer != enduserUpdateBody.idCustomer) {
							location.href = '/home';
						}

						this.user = userData;
					});
				});
			}

			this.updateEmailLogStatus();
			this.updateAnalystShowAwaitingAuthorization();
		}
	}

	updateEmailLogStatus(): void {
		const user = this.profileService.userData();
		const updates = [];

		if (user.sessionStatus === 0 || user.sessionStatus === 1) {
			// Quando ambas as sessões estão ativas
			if (user.idAnalyst && user.id) {
				updates.push(this.profileService.putEnduserEmailsLog(this.receiveEmailLog));
				updates.push(this.profileService.putAnalystEmailsLog(this.receiveEmailLog));
			}

			// Verificar se apenas `user.idAnalyst` está definido, mas `user.id` não está
			if (user.idAnalyst && !user.id) {
				updates.push(this.profileService.putAnalystEmailsLog(this.receiveEmailLog));
			}

			// Verificar se apenas `user.id` está definido, mas `user.idAnalyst` não está
			if (user.id && !user.idAnalyst) {
				updates.push(this.profileService.putEnduserEmailsLog(this.receiveEmailLog));
			}
		}

		// Executa todas as atualizações
		if (updates.length > 0) {
			forkJoin(updates).subscribe(
				() => {},
				(error) => {
					console.error('Ocorreu um erro durante as atualizações', error);
				}
			);
		}
	}

	updateAnalystShowAwaitingAuthorization(): void {
		// Verifica se sessionStatus é 1
		if (this.user.sessionStatus.toString() == '1') {
			this.profileService.putAnalystShowAwaitingAuthorization(this.showAwaitingAuthorization).subscribe(() => {});
		}
	}
}
